import React, { JSX, useState } from 'react'
import * as diff from 'diff'
import PropTypes from 'prop-types'
import Word from '../containers/RenderStroyComponent/Word'
window.diff = diff

// Helper function to split Chinese text into individual characters/words
const splitChineseText = (text: string): string[] => {
  return Array.from(text)
}

// Helper function to create word objects similar to what OneLineContent expects
const createWordObject = (char, pinyin = '', english = '') => {
  return {
    word: char,
    pinyin: pinyin,
    english: english,
  }
}

/** The highlight for an added or removed run (a removal wins, as it always has). */
const changeClass = (added?: boolean, removed?: boolean) =>
  removed ? 'orange-active text-strike-out' : added ? 'green-active' : ''

const Diff = ({
  string1 = '',
  string2 = '',
  mode = 'characters',
  chineseWordFontSize,
  handleClickWord = null,
  showPinYin = true,
}: any): JSX.Element => {
  const [activeIndex] = useState('-1')
  let groups: diff.Change[] = []

  if (mode === 'characters') groups = diff.diffChars(string1, string2)
  if (mode === 'words') groups = diff.diffWords(string1, string2)

  // Process diff results to create word components
  const mappedNodes = groups.map((group, index) => {
    const { value, added, removed } = group

    // For non-Chinese characters or punctuation, render as simple spans
    if (/^[^\u4e00-\u9fa5]+$/.test(value) && value.trim() !== '') {
      let nodeStyles = ''
      if (added) nodeStyles = 'green-active'
      if (removed) nodeStyles = 'orange-active text-strike-out'

      return (
        <span
          style={{ fontSize: chineseWordFontSize?.newWordsExample, lineHeight: '1.5', letterSpacing: '1.5px' }}
          key={`diff${index}`}
          className={nodeStyles}
        >
          {value}
        </span>
      )
    }

    // For Chinese characters, split into individual characters and render as Word components
    const characters = splitChineseText(value)

    return (
      <React.Fragment key={`diff-group-${index}`}>
        {characters.map((char, charIndex) => {
          // Skip rendering empty characters
          if (!char.trim()) return null

          // Create a unique index for each character
          const charKey = `diff-${index}-${charIndex}`

          // Create word object for the character
          const wordObj = createWordObject(char)

          // For Chinese characters, use Word component with appropriate styling
          if (/[\u4e00-\u9fa5]/.test(char)) {
            // Instead of using a customClass prop, we'll use the existing className construction in Word
            // We'll use the isNewWord, isOldWord, etc. props to apply styling
            return (
              <span key={charKey} className={added ? 'green-active' : removed ? 'orange-active text-strike-out' : ''}>
                <Word
                  key={charKey}
                  index={charKey}
                  isActive={charKey === activeIndex}
                  text={char}
                  textObj={wordObj}
                  sentence={[wordObj]}
                  onClick={handleClickWord}
                  pinYinText={showPinYin ? wordObj?.pinyin : null}
                  chineseWordFontSize={chineseWordFontSize?.newWordsExample || '20px'}
                />
              </span>
            )
          } else {
            // For non-Chinese characters within a Chinese segment
            const nodeStyles = changeClass(added, removed)

            return (
              <span
                key={charKey}
                className={nodeStyles}
                style={{ fontSize: chineseWordFontSize?.newWordsExample, lineHeight: '1.5', letterSpacing: '1.5px' }}
              >
                {char}
              </span>
            )
          }
        })}
      </React.Fragment>
    )
  })

  return <span className='diff-container'>{mappedNodes}</span>
}

Diff.propTypes = {
  string1: PropTypes.string,
  string2: PropTypes.string,
  mode: PropTypes.oneOf(['characters', 'words']),
  chineseWordFontSize: PropTypes.object,
  story: PropTypes.object,
  handleClickWord: PropTypes.func,
  showPinYin: PropTypes.bool,
}

export default Diff
